import spacetime, { Spacetime, SpacetimeWithI18n, TimeUnit, TimezoneMeta } from 'spacetime'

const FALLBACK_TIMEZONE = 'America/New_York'

/**
 * The purpose of this module is to effectively isolate most, if not all, usages of our 3rd party DateTime library (currently Spacetime) so we can easily swap out if ever needed
 * When adding new methods, leverage getTime() and getCurrentTime() to ensure that we fallback appropriately when we try to lookup a non-ISO timezone (ex. 'etc/unknown')
 */
export default {

  /**
   * Takes a epoch dateTime and converts it to a Spacetime object and
   * Falls back to FALLBACK_TIMEZONE if the timezone provided is bogus
   */
  getTime(dateTime: number, timezone?: string): Spacetime {
    try {
      return !!timezone ? spacetime(dateTime, timezone) : spacetime(dateTime)
    } catch(err){
      return spacetime(dateTime, FALLBACK_TIMEZONE)
    }
  },

  /**
   * Gets the Spacetime object representing the current point in timezone
   * If you want the actual dateTime number, use the getCurrentTimeEpoch method below instead
   */
  getCurrentTime(timezone?: string): Spacetime {
    try {
      return spacetime.now(timezone || this.getTimezone())
    } catch(err) {
      return spacetime.now(FALLBACK_TIMEZONE)
    }
  },

  getCurrentTimeEpoch() {
    return this.getCurrentTime().epoch
  },

  /**
   * Attempts to grab the timezone via Spacetime's lookup (which uses a couple methods including Intl.DateTimeFormat under the hood)
   * If the timezone returned is not valid, use FALLBACK_TIMEZONE instead
   */
  getTimezone() :string {
    try {
      const timezone :TimezoneMeta = spacetime().timezone()
      return timezone.name
    } catch(err) {
      return FALLBACK_TIMEZONE
    }
  },

  /**
   * Gets the numerical offset for the current timezone
   */
  getOffsetForTimezone(timezone: string): number {
    return this.getCurrentTime(timezone).offset()
  },

  /**
   * Gets the numerical time difference in days from a provided dateTime from the current moment
   * @returns {number} - number of days difference
   */
  getCurrentTimeDifferenceInDays(sinceTime: number): number {
    if (!sinceTime) {
      return 0
    }

    return this.getCurrentTime().since(sinceTime || this.getCurrentTime()).diff.days
  },

  /**
   * Adds a certain amount of time to a given moment
   * Allows customization by both amount of time, and type of time (ex. second, day, hour)
   * @returns {number} epoch dateTime
   */
  addTime(dateTime: number, timeAmount: number, timeType: TimeUnit): number {
    return this.getTime(dateTime).add(timeAmount, timeType).epoch
  },

  /**
   * Given two times, checks if the first time is before the second time
   */
  isTimeBefore(dateTime: number, afterDateTime: number): boolean {
    const convertedAfterDateTime = this.convertToSpacetime(afterDateTime)
    return this.getTime(dateTime).isBefore(convertedAfterDateTime)
  },

  /**
   * Given two times, checks if the first time is after the second time
   */
  isTimeAfter(dateTime: number, afterDateTime: number) {
    const convertedDateTime = this.convertToSpacetime(dateTime)
    const convertedAfterDateTime = this.convertToSpacetime(afterDateTime)

    return convertedDateTime.isAfter(convertedAfterDateTime)
  },

  /**
   * Given two times, checks if the first time is after the second time plus {x} number of hours
   */
  isTimeAfterHours(dateTime: number, afterDateTime: number, hourCount: number): boolean {
    return this.isTimeAfter(dateTime,
      this.addTime(afterDateTime, hourCount, 'hour')
    )
  },

  /**
   * Given two times, checks if the first time is after the second time plus {x} number of seconds
   */
  isTimeAfterSeconds(dateTime: number, afterDateTime: number, secondCount: number): boolean {
    return this.isTimeAfter(dateTime,
      this.addTime(afterDateTime, secondCount, 'second')
    )
  },

  /**
   * Given a dateTime, check if it's before the current dateTime
   */
  isCurrentTimeAfter(afterDateTime: number): boolean {
    const convertedAfterDateTime = this.convertToSpacetime(afterDateTime)

    return this.getCurrentTime().isAfter(convertedAfterDateTime)
  },

  /**
   * Given a datetime, checks to see if it's within the same day as the current dateTime
   */
  isTimeToday(dateTime: number): boolean {
    try {
      return this.getTime(dateTime).isSame(spacetime.today(this.getTimezone()), 'date')
    } catch(err) {
      return this.getTime(dateTime).isSame(spacetime.today(FALLBACK_TIMEZONE), 'date')
    }
  },

  /**
   * Given a datetime, checks to see if it has the same unit as the current time
   *
   * Ie. Use this to check if a time is wihin the same hour/minute/day/week/month as the current time
   */
  isTimeSame(dateTime: number, timeType: TimeUnit = 'date'): boolean {
    return this.getTime(dateTime).isSame(this.getCurrentTime(), timeType)
  },

  /**
   * Given a datetime, formats it into different human-readable formats
   *
   * For a full list, see https://github.com/spencermountain/spacetime#date-formatting
   */
  unixFormatDateTime(dateTime: number, format: string, timezone?: string): string {
    return this.getTime(dateTime, timezone).unixFmt(format)
  },

  /**
   * Handles swapping date format text when the widget language is changed
   */
  compileI18nForDateTime(i18n) {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const months = [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december'
    ]

    const run = () => {
      (spacetime(null) as SpacetimeWithI18n).i18n({
        days: {
          long: days.map(day => i18n.t(`dt.day.${day}`)),
          short: days.map(day => i18n.t(`dt.short.day.${day}`))
        },
        months: {
          long: months.map(month => i18n.t(`dt.month.${month}`)),
          short: months.map(month => i18n.t(`dt.short.month.${month}`))
        },
        useTitleCase:false
      })
    }

    run()
  },

  convertToSpacetime(dateTime:number): Spacetime {
    return this.getTime(dateTime)
  },

  convertToSpacetimeWithTimezone(dateTime:number, timezone:string): Spacetime {
    return this.getTime(dateTime, timezone)
  }
}
